import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Button from './Button';

/**
 * Delete-athlete modal launched from the guardian dashboard.
 *
 * Two stages:
 *   1. Initial confirmation — typing the athlete's first name to confirm.
 *   2. Apple-sub warning (only if the API returns requires_confirmation=true).
 *
 * On success, calls onDeleted() and the parent reloads its data.
 */
export default function DeleteAthleteModal({ athlete, onClose, onDeleted }) {
  const { API_URL } = useAuth();
  const [stage, setStage] = useState('initial'); // 'initial' | 'appleWarning'
  const [typed, setTyped] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [warningData, setWarningData] = useState(null);

  const firstName = athlete.athlete_first_name || athlete.athlete_email || '';
  const fullName = [athlete.athlete_first_name, athlete.athlete_last_name]
    .filter(Boolean)
    .join(' ') || athlete.athlete_email;

  const callDelete = async (confirmWarnings) => {
    setWorking(true);
    setError('');
    try {
      const res = await axios.delete(
        `${API_URL}/guardians/athletes/${athlete.athlete_user_id}`,
        { params: { confirm_warnings: confirmWarnings ? 'true' : 'false' } },
      );
      if (res.data && res.data.requires_confirmation) {
        setWarningData(res.data);
        setStage('appleWarning');
      } else {
        onDeleted();
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Delete failed.');
    } finally {
      setWorking(false);
    }
  };

  const overlayStyle = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  };
  const modalStyle = {
    background: '#fff', borderRadius: 12, padding: 24, maxWidth: 480, width: '92%',
  };

  return (
    <div style={overlayStyle} onClick={() => { if (!working) onClose(); }}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {stage === 'initial' && (
          <>
            <h2 style={{ marginTop: 0 }}>Delete {fullName}?</h2>
            <p style={{ color: '#64748b', fontSize: 14 }}>
              This permanently removes their profile, photos, videos, stats, and messages.
              This cannot be undone.
            </p>
            <p style={{ fontSize: 13, marginTop: 16 }}>
              Type <strong>{firstName}</strong> to confirm:
            </p>
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              style={{
                width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 8,
                fontSize: 14, marginBottom: 12,
              }}
              autoFocus
            />
            {error && <p style={{ color: '#dc2626', fontSize: 13 }}>{error}</p>}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Button type="button" variant="secondary" onClick={onClose} disabled={working}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="danger"
                onClick={() => callDelete(false)}
                disabled={typed !== firstName}
                loading={working}
                loadingText="Deleting…"
              >
                Delete
              </Button>
            </div>
          </>
        )}

        {stage === 'appleWarning' && warningData && (
          <>
            <h2 style={{ marginTop: 0, color: '#b45309' }}>
              ⚠ Your Apple subscription won't be cancelled automatically
            </h2>
            <p style={{ color: '#64748b', fontSize: 14 }}>
              Deleting this account removes the profile and data, but the subscription on
              your Apple ID will continue to charge until you cancel it.
            </p>
            <Button
              href={warningData.manage_subscriptions_url || 'https://apps.apple.com/account/subscriptions'}
              target="_blank"
              rel="noreferrer"
              variant="secondary"
              style={{ display: 'inline-block', margin: '12px 0' }}
            >
              Manage Subscriptions
            </Button>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, margin: '12px 0' }}>
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
              />
              I understand I need to cancel my subscription separately
            </label>
            {error && <p style={{ color: '#dc2626', fontSize: 13 }}>{error}</p>}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Button type="button" variant="secondary" onClick={onClose} disabled={working}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="danger"
                onClick={() => callDelete(true)}
                disabled={!acknowledged}
                loading={working}
                loadingText="Deleting…"
              >
                Delete Anyway
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
